import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import mermaid from 'mermaid'
import * as echarts from 'echarts'
import { renderMarkdownFull } from '../../features/markdown/services/markdownRenderer'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { useAppStore } from '../../store/useAppStore'

interface CodeEditorModalProps {
  isOpen: boolean
  onClose: () => void
  initialCode: string
  language: string
  onSave: (code: string) => void
}

export const CodeEditorModal: React.FC<CodeEditorModalProps> = ({
  isOpen,
  onClose,
  initialCode,
  language,
  onSave
}) => {
  const { t } = useTranslation()
  const [code, setCode] = useState(initialCode)
  const [svg, setSvg] = useState('')
  const [renderedMarkdown, setRenderedMarkdown] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [scale, setScale] = useState(1)
  const renderRef = useRef<HTMLDivElement>(null)
  
  const theme = useAppStore(state => state.theme)
  const isDark = theme === 'dark' || (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)

  useEffect(() => {
    if (isOpen) {
      setCode(initialCode)
    }
  }, [isOpen, initialCode])

  useEffect(() => {
    if (!isOpen) return

    if (language === 'mermaid') {
      const renderDiagram = async () => {
        try {
          mermaid.initialize({
            startOnLoad: false,
            securityLevel: 'loose',
            theme: isDark ? 'dark' : 'default',
            fontFamily: 'inherit',
            themeVariables: isDark ? {
              primaryColor: '#3b82f6',
              primaryTextColor: '#f3f4f6',
              primaryBorderColor: '#2563eb',
              lineColor: '#9ca3af',
              secondaryColor: '#1e293b',
              tertiaryColor: '#111827'
            } : undefined
          })
  
          // Generate a unique ID for each render to avoid conflicts
          const id = `mermaid-preview-${Date.now()}`
          const { svg } = await mermaid.render(id, code)
          setSvg(svg)
          setError(null)
        } catch (err) {
          console.error('Mermaid render error:', err)
          setError(err instanceof Error ? err.message : 'Unknown error')
        }
      }
  
      const timeoutId = setTimeout(renderDiagram, 500) // Debounce
      return () => clearTimeout(timeoutId)
    } else if (language === 'echarts') {
      const renderChart = () => {
        const container = renderRef.current
        if (!container) return

        try {
          const option = JSON.parse(code)
          
          // 确保容器有尺寸
          container.style.height = '400px'
          container.style.width = '100%'

          let chart = echarts.getInstanceByDom(container)
          
          if (chart) {
            chart.dispose()
          }

          chart = echarts.init(container, isDark ? 'dark' : undefined, {
            renderer: 'svg'
          })

          chart.setOption(option, true)
          setError(null)

          return () => {
            chart?.dispose()
          }
        } catch (err) {
          console.error('ECharts render error:', err)
          setError(err instanceof Error ? err.message : 'Please check if the configuration is valid JSON')
        }
      }

      const timeoutId = setTimeout(renderChart, 300)
      return () => {
        clearTimeout(timeoutId)
        const container = renderRef.current
        if (container) {
          const chart = echarts.getInstanceByDom(container)
          chart?.dispose()
        }
      }
    } else if (language === 'markdown-render') {
      const renderMd = async () => {
        try {
          const html = await renderMarkdownFull(code, isDark)
          setRenderedMarkdown(html)
          setError(null)
        } catch (err) {
          console.error('Markdown render error:', err)
          setError(err instanceof Error ? err.message : 'Unknown error')
        }
      }

      const timeoutId = setTimeout(renderMd, 300) // Debounce
      return () => clearTimeout(timeoutId)
    }
  }, [code, isOpen, isDark, language])

  const handleSave = () => {
    onSave(code)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab key support
    if (e.key === 'Tab') {
      e.preventDefault()
      const textarea = e.currentTarget
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const value = textarea.value
      const newValue = value.substring(0, start) + '  ' + value.substring(end)
      setCode(newValue)
      // Restore cursor position
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 2
      }, 0)
    }
    
    // Cmd/Ctrl + S to save
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault()
      handleSave()
    }
  }

  const getPreviewTitle = () => {
    if (language === 'mermaid' || language === 'echarts' || language === 'markdown-render') {
      return t('preview.livePreview', 'Live Preview')
    }
    return t('preview.sourceCode', 'Source Code')
  }

  const renderPreview = () => {
    if (language === 'mermaid') {
      return (
        <div className="flex-1 overflow-auto bg-white dark:bg-gray-800 relative flex">
          <div className="m-auto p-4">
            {error ? (
              <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                {error}
              </div>
            ) : (
              <div 
                ref={renderRef}
                dangerouslySetInnerHTML={{ __html: svg }} 
                className="flex items-center justify-center origin-center transition-transform duration-200"
                style={{ zoom: scale }}
              />
            )}
          </div>
          {/* Zoom Controls */}
          <div className="absolute bottom-4 right-4 flex items-center space-x-1 bg-white dark:bg-gray-800 p-1 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              onClick={() => setScale(s => Math.max(0.1, s - 0.1))}
              title={t('preview.zoomOut', 'Zoom Out')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
            <span className="text-xs text-gray-500 dark:text-gray-400 w-10 text-center select-none">
              {Math.round(scale * 100)}%
            </span>
            <button
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              onClick={() => setScale(s => Math.min(5, s + 0.1))}
              title={t('preview.zoomIn', 'Zoom In')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
            </button>
            <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1"></div>
            <button
              className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              onClick={() => setScale(1)}
              title={t('preview.resetZoom', 'Reset Zoom')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 3 21 3 21 9"></polyline>
                <polyline points="9 21 3 21 3 15"></polyline>
                <line x1="21" y1="3" x2="14" y2="10"></line>
                <line x1="3" y1="21" x2="10" y2="14"></line>
              </svg>
            </button>
          </div>
        </div>
      )
    }

    if (language === 'echarts') {
      return (
        <div className="flex-1 overflow-auto bg-white dark:bg-gray-800 relative">
          {error ? (
            <div className="text-red-500 text-sm p-4 m-4 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
              {error}
            </div>
          ) : (
            <div 
              ref={renderRef}
              className="w-full h-full flex items-center justify-center"
            />
          )}
        </div>
      )
    }

    if (language === 'markdown-render') {
       return (
         <div className="flex-1 overflow-auto bg-white dark:bg-gray-800 relative p-6">
           <div className="markdown-body [&_.preview-actions]:!hidden [&_.copy-button-wrapper]:!m-0 [&_.copy-button-wrapper]:!p-0 [&_.copy-button-wrapper]:!border-0">
              {error ? (
               <div className="text-red-500 text-sm p-4 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                 {error}
               </div>
            ) : (
              <div 
                dangerouslySetInnerHTML={{ __html: renderedMarkdown }} 
              />
            )}
          </div>
        </div>
      )
    }

    // Generic code preview (read-only)
    return (
      <div className="flex-1 overflow-auto bg-[#f7f6f3] dark:bg-[#252525] relative">
        <pre className="p-4 m-0 h-full overflow-visible">
          <code className={`language-${language} font-mono text-sm leading-relaxed text-[#37352f] dark:text-[#e3e2df]`}>
            {code}
          </code>
        </pre>
      </div>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('preview.internalEdit')}
      size="xl"
      zIndex={60}
      className=""
      contentClassName="!max-w-[90vw] !h-[85vh] flex flex-col"
      bodyClassName="!p-0 flex flex-col"
      footer={
        <div className="flex justify-end space-x-3 w-full">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {t('common.save', 'Save')}
          </Button>
        </div>
      }
    >
      <div className="flex-1 flex min-h-0">
        {/* Editor */}
        <div className="w-1/2 flex flex-col border-r border-gray-200 dark:border-gray-700">
          <div className="p-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500">
            {language === 'mermaid' ? t('preview.mermaidCode', 'Mermaid Code') : language === 'echarts' ? 'ECharts Configuration' : t('editor.code', 'Code')}
          </div>
          <textarea
            className="flex-1 w-full p-4 resize-none focus:outline-none bg-white dark:bg-gray-800 font-mono text-sm"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={handleKeyDown}
            spellCheck={false}
          />
        </div>

        {/* Preview */}
        <div className="w-1/2 flex flex-col bg-white dark:bg-gray-800 relative group">
          <div className="p-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 flex justify-between items-center">
            <span>{getPreviewTitle()}</span>
            {language === 'mermaid' && (
              <div className="text-xs text-gray-400">
                {Math.round(scale * 100)}%
              </div>
            )}
          </div>
          
          {renderPreview()}
        </div>
      </div>
    </Modal>
  )
}
